<script setup lang="ts">
import { ref } from 'vue';
import { getCurrentLocale } from '@/i18n';
import { getTextVariant } from '@/utils/stringUtils';
import { useConvertTextComposable } from '@/components/modal/useConvertTextComposable';
import { useWorksheetComposable } from './useWorksheetComposable';
import { formatDate } from '@/utils/dateUtils';
import NumberWorksheetEditor from './NumberWorksheetEditor.vue';

interface Props {
  tour: any;
  toursInfo?: any;
  tourWorkSheet: any;
}
const props = defineProps<Props>();

const emits = defineEmits<{ changeNumber: [value: any] }>();

const { handleReplaceValueToName } = useConvertTextComposable();
const { authorizations } = useWorksheetComposable();
const lang = getCurrentLocale();
const canEdit = authorizations?.worksheet_tour_quote_header?.pax_room_quantity_edit;

const data = ref<any>({
  adtNo: props.tour.adtNo,
  chdNo: props.tour.chdNo,
  numOfRooms: props.tour.numOfRooms
});

const changeNumber = (event: number, type: string) => {
  data.value[type] = event;
  emits('changeNumber', { value: event, type: type });
};

const renderTourDetail = (tour: any) => {
  const tourDescription = getTextVariant(lang, tour?.tourInclusionEn, tour?.tourInclusionFr);
  const tourType = tour.tourType ? ` - ${handleReplaceValueToName(tour.tourType, 'tourType') ?? ''}` : '';
  const guided = tour.guided ? ` - ${handleReplaceValueToName(tour.guided?.toUpperCase(), 'guided') ?? ''}` : '';
  const roomCode = ` (${convertCode(tour.ttsId, 'room')})`;

  return `${tourDescription}${tourType}${guided}${roomCode}`;
};

const convertCode = (ttsId: string, type: string) => {
  return type === 'prod' ? ttsId.slice(0, 6) : ttsId.slice(6);
};
</script>
<template>
  <div>
    <div class="option">
      <div class="lead-option">{{ $t('common.tour') }}: {{ getTextVariant(lang, tour?.tourNameEn, tour?.tourNameFr) }} ({{ formatDate(tour.depDate, 'dd-LLL-yyyy', false, lang) }}) - {{ convertCode(tour.ttsId, 'prod') }}</div>
      <div class="detail-option flex justify-content-between flex-wrap lg:flex-nowrap">
        <div>
          <div class="font-bold mt-2 mb-2">
            <div class="flex gap-3">
              <div class="min-w-label--new font-bold">{{ $t('common.gateway') }}:</div>
              <div class="font-normal">{{ getTextVariant(lang, toursInfo.gate, toursInfo.gateFr) }} ({{ toursInfo.gateCode }})</div>
            </div>
          </div>
          <div class="font-bold mb-2">
            <div class="flex gap-3">
              <div class="min-w-label--new font-bold">{{ $t('common.destination') }}:</div>
              <div class="font-normal">{{ getTextVariant(lang, toursInfo.dest, toursInfo.destFr) }} ({{ toursInfo.airportCode }})</div>
            </div>
          </div>
          <div class="font-bold mb-2">
            <div class="flex gap-3">
              <div class="min-w-label--new font-bold">{{ $t('worksheet.departure_date') }}:</div>
              <div class="font-normal">{{ formatDate(tour.depDate, 'dd-LLL-yyyy', false, lang) }} ({{ getTextVariant(lang, tour?.tourDurationEn, tour?.tourDurationFr) }})</div>
            </div>
          </div>
          <div class="tour-detail font-bold mb-2">
            <div class="flex gap-3">
              <div class="min-w-label--new font-bold">{{ $t('worksheet.tour_detail') }}:</div>
              <div class="font-normal">{{ renderTourDetail(tour) }}</div>
            </div>
          </div>
        </div>
        <div class="flex flex-column align-items-end">
          <div class="brochure font-bold mt-2">
            <div class="flex">
              <div class="min-w-label">{{ $t('worksheet.brochure') }}:</div>
              <div class="font-normal w-6rem">{{ toursInfo.brochure }}</div>
            </div>
          </div>
          <div class="adults">
            <NumberWorksheetEditor :title="$t('common.adult')" :number="data.adtNo" :permission="canEdit" @changeNumber="changeNumber($event, 'adtNo')" />
          </div>
          <div class="child">
            <NumberWorksheetEditor :title="$t('common.children')" :number="data.chdNo" :permission="canEdit" @changeNumber="changeNumber($event, 'chdNo')" />
          </div>
          <div class="total font-bold mb-2">
            <div class="flex">
              <div class="min-w-label">{{ $t('worksheet.total_passengers') }}:</div>
              <div class="font-normal w-6rem">{{ data.adtNo + data.chdNo }}</div>
            </div>
          </div>
          <div class="number-room font-bold mb-2">
            <NumberWorksheetEditor :title="$t('advance_quote.form.number_of_room')" :number="data.numOfRooms" :permission="canEdit" @changeNumber="changeNumber($event, 'numOfRooms')" />
          </div>
        </div>
      </div>
      <div class="additionalNote font-bold mb-2">
        <div class="flex gap-3">
          <div class="min-w-label--new">{{ $t('advance_quote.form.additional_note') }}:</div>
          <div class="font-normal w-full border-1 border-solid border-black p-3">{{ toursInfo.additionalNote }}</div>
        </div>
      </div>
    </div>
  </div>
</template>
